import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { SearchBar } from "@/components/SearchBar";
import { ProductCard } from "@/components/ProductCard";
import { FLAGS } from "@/lib/format";

export default function SearchResults() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ total: 0, results: [] });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const type = params.get("type") || "all";
  const category = params.get("category") || "";
  const country = params.get("country") || "";
  const inStock = params.get("in_stock") === "true";
  const sort = params.get("sort") || "price_asc";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const fetchResults = useCallback(() => {
    setLoading(true);
    const p = { sort };
    if (q) p.q = q;
    if (type) p.type = type;
    if (category) p.category = category;
    if (country) p.country = country;
    if (inStock) p.in_stock = true;
    api.get("/search", { params: p }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [q, type, category, country, inStock, sort]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6"><SearchBar variant="hero" /></div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* Filters */}
        <aside className="h-fit rounded-lg border border-border bg-white p-6">
          <div className="flex items-center gap-2 font-heading font-semibold tracking-tight text-slate-900">
            <SlidersHorizontal className="h-4 w-4" /> {t("search.filters")}
          </div>

          <div className="mt-5">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("search.category")}</label>
            <select data-testid="filter-category" value={category} onChange={(e) => update("category", e.target.value)}
              className="mt-2 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <option value="">{t("search.all")}</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name} ({c.count})</option>)}
            </select>
          </div>

          <div className="mt-5">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("search.country")}</label>
            <select data-testid="filter-country" value={country} onChange={(e) => update("country", e.target.value)}
              className="mt-2 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <option value="">{t("search.allc")}</option>
              {["SK", "CZ", "PL", "HU"].map((c) => <option key={c} value={c}>{FLAGS[c]} {c}</option>)}
            </select>
          </div>

          <label className="mt-5 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input data-testid="filter-instock" type="checkbox" checked={inStock} onChange={(e) => update("in_stock", e.target.checked ? "true" : "")}
              className="h-4 w-4 rounded border-border text-primary" />
            {t("search.instock")}
          </label>
        </aside>

        {/* Results */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-slate-500"><span className="font-semibold text-slate-900">{data.total}</span> {t("search.results")}</p>
            <select data-testid="sort-select" value={sort} onChange={(e) => update("sort", e.target.value)}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <option value="price_asc">{t("search.sort.price_asc")}</option>
              <option value="price_desc">{t("search.sort.price_desc")}</option>
              <option value="offers">{t("search.sort.offers")}</option>
              <option value="name">{t("search.sort.name")}</option>
            </select>
          </div>

          {loading ? (
            <div className="py-20 text-center text-slate-400">{t("common.loading")}</div>
          ) : data.results.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-white py-20 text-center text-slate-500" data-testid="search-empty">{t("search.empty")}</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {data.results.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
